/**
 * Recordes do jogador — o que vale a pena carregar de um aparelho para outro.
 *
 * Vive no `localStorage` sob o prefixo `pixelraid:`, que é o que o
 * `account-service` sincroniza (ver `.infra/CONTRATO-login-compartilhado.md`).
 * Antes disto o save na nuvem existia mas não carregava nada: as únicas chaves
 * eram `muted`, `crt` e `autofire` — três booleanos de preferência. "Continuar
 * em outro dispositivo" sem recorde não entrega nada que o jogador perceba.
 *
 * DUAS REGRAS DO CONTRATO mandam no formato daqui:
 *
 * 1. **Teto de 256 KB por slot.** O histórico do Diário é podado a
 *    `MAX_DAILY` dias. Sem poda ele cresce para sempre e, no dia em que
 *    estourar, o save inteiro do jogador passa a ser rejeitado com 413 — e o
 *    sintoma aparece longe da causa.
 * 2. **Nada de identidade nem direito de acesso.** Aqui só entram números de
 *    partida. Identidade de aparelho sincronizada colide entre dois clientes;
 *    direito que vem do save é direito falsificável.
 *
 * Não é simulação: pode ler relógio e `localStorage` à vontade. O core não.
 */

const KEY = 'pixelraid:records'

/** Dias de Diário guardados. 60 entradas ≈ 4 KB, bem longe do teto. */
const MAX_DAILY = 60

export interface RunResult {
  readonly score: number
  readonly distPx: number
  readonly sections: number
}

interface Best extends RunResult {
  /** Data ISO do dia em que o recorde foi feito. Só para exibição. */
  readonly at: string
}

export interface Records {
  /** Melhor corrida no modo Clássico (seed pública, comparável entre pessoas). */
  classic: Best | null
  /** Melhor corrida em seed aleatória — não comparável, mas é o orgulho do jogador. */
  random: Best | null
  /** Resultado por dia do Diário, chaveado por `AAAA-MM-DD`. */
  daily: Record<string, RunResult>
}

const empty = (): Records => ({ classic: null, random: null, daily: {} })

export function loadRecords(): Records {
  try {
    const raw = localStorage.getItem(KEY)
    if (raw === null) return empty()
    const parsed = JSON.parse(raw) as Partial<Records>
    return {
      classic: parsed.classic ?? null,
      random: parsed.random ?? null,
      daily: parsed.daily ?? {},
    }
  } catch {
    // Save corrompido (ou vindo de versão futura) não pode impedir de jogar.
    return empty()
  }
}

function save(records: Records): void {
  // Poda ANTES de gravar: a chave é a data ISO, então ordem alfabética é
  // ordem cronológica e o corte pega sempre os mais antigos.
  const dias = Object.keys(records.daily).sort()
  for (const dia of dias.slice(0, Math.max(0, dias.length - MAX_DAILY))) {
    delete records.daily[dia]
  }
  try {
    localStorage.setItem(KEY, JSON.stringify(records))
  } catch {
    // Cota cheia ou modo privado: perder recorde é ruim, travar é pior.
  }
}

/** Uma corrida vence a outra por distância; score desempata (§12.3). */
const melhor = (a: RunResult, b: Best | null): boolean =>
  b === null || a.distPx > b.distPx || (a.distPx === b.distPx && a.score > b.score)

/**
 * Registra o fim de uma partida. `daily` é a data do desafio, ou null.
 * `classicSeed` diz se a seed é a pública do modo Clássico.
 *
 * Devolve `true` quando bateu recorde — é o que o HUD usa para comemorar.
 */
export function recordRun(
  run: RunResult,
  options: { daily: string | null; isClassic: boolean },
): boolean {
  const records = loadRecords()
  const at = new Date().toISOString().slice(0, 10)

  if (options.daily !== null) {
    // O Diário é uma tentativa por dia: a primeira vale, repetição não
    // sobrescreve para melhor — senão o desafio vira "tente até acertar".
    const jaTem = records.daily[options.daily] !== undefined
    if (!jaTem) records.daily[options.daily] = run
    save(records)
    return !jaTem
  }

  const alvo = options.isClassic ? 'classic' : 'random'
  if (!melhor(run, records[alvo])) {
    save(records)
    return false
  }
  records[alvo] = { ...run, at }
  save(records)
  return true
}
